import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { openSnackbar } from '../../../../features/SnackBar/snackBarSlice';
import { resetHealthScoreDetails } from '../../../../features/HealthScore/HealthScoreSlice';
import { addHealthScore, getHealthInsightsDetails, updateHealthScore } from '../../../../features/HealthScore/HealthScoreAction';
import { getFeaturesList, getPackageList } from '../../../../features';

const validationSchema = Yup.object().shape({
  healthScoreLabel: Yup.string().required('KPI Score Label is Required!'),
  healthScoreRules: Yup.array().of(
    Yup.object().shape({
      ruleType: Yup.string().required('rule type is required !'),
      targetMetric: Yup.string().required('target metric is required !'),
      threshold: Yup.array().of(Yup.number().test('positive', 'Value must be positive', (value) => value >= 0).required('Please enter threshold value')).test('checkOrder', 'Values must be in increasing order', (value) => {
        return !value || value.length < 2 || value[0] < value[1];
      }),
      rulePoints: Yup.string().required('rule points is required !'),
    }),
  ),
});

const initialRuleState = {
  healthScoreLabel: '',
  healthScoreRules: [{
    ruleType: '',
    ruleOption: '',
    targetMetric: '',
    threshold: [],
    rulePoints: '',
    isOpenGraph: false,
  }],
};

const useAddEditHeathInsights = () => {
  const [featureManagerListData, setFeatureManagerListData] = useState([]);
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEditPage = pathname.includes('update');
  const isAddPage = pathname.includes('new');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const healthScoreDetails = useSelector((state) => state.healthInsightsDetails.healthInsightsDetails.healthScoreDetails);
  const featureManagerList = useSelector((state) => state.featureManagerList.featureManagerList);

  const scoreControlsEnabled = useSelector((state) => state.scoreControls?.enabled);

  const [healthScoreDetailsData, setHealthScoreDetailsData] = useState({
    healthScoreLabel: '',
    healthScoreRules: [],
  });

  const [healthScoreCriteria, setHealthScoreCriteria] = useState([]);
  const handleAddCriteriaRow = useCallback(() => {
    setHealthScoreCriteria((prevRows) => [...prevRows, {
      criteria: '', operator: '', value: '', andOr: '&&',
    }]);
  }, []);

  useEffect(() => {
    dispatch(getPackageList());
  }, []);

  useEffect(() => {
    const updatedArray = healthScoreDetails?.healthScoreRules?.map((ruleData) => {
      const obj = { ...ruleData };
      obj.threshold = JSON.parse(obj.threshold);
      return obj;
    });
    setHealthScoreDetailsData({
      healthScoreLabel: healthScoreDetails?.healthScoreLabel,
      healthScoreRules: updatedArray,
    });
    if (healthScoreDetails?.healthScoreCriteria) {
      setHealthScoreCriteria(healthScoreDetails?.healthScoreCriteria);
    }
  }, [healthScoreDetails]);

  useEffect(() => {
    if (isEditPage) {
      dispatch(getHealthInsightsDetails({ healthScoreId: id }));
    }
    return () => {
      dispatch(resetHealthScoreDetails());
    };
  }, [dispatch, id, isEditPage]);

  const handleSubmit = (values) => {
    const data = {
      healthScoreLabel: values.healthScoreLabel,
      healthScoreRules: values.healthScoreRules,
      healthScoreCriteria,
    };
    dispatch(addHealthScore({ data })).then((res) => {
      const { payload } = res;
      if (res.error) {
        dispatch(openSnackbar({ open: true, message: payload, type: 'error' }));
      } else {
        navigate('/kpi-score-configuration');
        dispatch(openSnackbar({ open: true, message: 'KPI Score Created Successfully', type: 'success' }));
      }
    });
  };

  const handleEditSubmit = (values) => {
    const data = {
      healthScoreId: id,
      healthScoreLabel: values.healthScoreLabel,
      healthScoreRules: values.healthScoreRules,
      healthScoreCriteria,
    };

    dispatch(updateHealthScore({ data })).then((res) => {
      const { payload } = res;
      if (res.error) {
        dispatch(openSnackbar({ open: true, message: payload, type: 'error' }));
      } else {
        navigate('/kpi-score-configuration');
        dispatch(openSnackbar({ open: true, message: 'KPI Score Updated Successfully', type: 'success' }));
      }
    });
  };

  useEffect(() => {
    dispatch(getFeaturesList());
  }, []);

  useEffect(() => {
    if (featureManagerList.length) {
      setFeatureManagerListData(featureManagerList);
    }
  }, [featureManagerList]);

  const handleRuleChange = useCallback((index, selectedOption, setFieldValue) => {
    setFieldValue(`healthScoreRules[${index}].ruleType`, selectedOption);
    setFieldValue(`healthScoreRules[${index}].targetMetric`, '');
    setFieldValue(`healthScoreRules[${index}].threshold`, []);
    setFieldValue(`healthScoreRules[${index}].rulePoints`, '');
    setFieldValue(`healthScoreRules[${index}].ruleOption`, '');
  }, []);

  return {
    scoreControlsEnabled,
    validationSchema,
    handleSubmit,
    isEditPage,
    isAddPage,
    healthScoreDetailsData,
    handleEditSubmit,
    featureManagerListData,
    handleRuleChange,
    initialRuleState,
    healthScoreCriteria,
    setHealthScoreCriteria,
    handleAddCriteriaRow,
    primaryHealthScore: healthScoreDetails?.primaryHealthScore,
  };
};

export default useAddEditHeathInsights;
